/**
 * ProjectHoursSummary - The hours picture for a project work order.
 *
 * The rule this component exists to make visible: a project's pool is charged
 * for hours actually WORKED, not hours planned. An open child work order's
 * allocated hours are an estimate — they are shown as "Estimated (open)" so the
 * manager can see what is earmarked, but they do not come off the balance.
 * Only when a work order closes out do its actual logged hours draw the pool
 * down, and at that point the project reports how far over or under estimate
 * the closed work landed.
 *
 * Rendered on the project detail page, the project list, and the "Project
 * Assignment" panel on work order create, so all three read the same numbers
 * off the same server-computed fields.
 *
 * @example
 * <ProjectHoursSummary
 *	 totalHours={100}
 *	 estimatedOpen={30}
 *	 actualUsed={26}
 *	 remaining={74}
 *	 projectedRemaining={44}
 *	 variance={6}
 *	 hasClosedWork
 * />
 */
import React from 'react';
import { StatTile } from '../../atoms/StatTile';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface ProjectHoursSummaryProps {
	/** The project's fixed pool of hours. */
	totalHours: number;
	/** Estimated hours committed to work orders that have not closed yet. */
	estimatedOpen: number;
	/** Actual hours logged against closed-out work orders. */
	actualUsed: number;
	/** totalHours − actualUsed. What is genuinely left in the pool. */
	remaining: number;
	/** remaining − estimatedOpen. The forward-looking balance. */
	projectedRemaining: number;
	/** actualUsed − estimated on closed work. Positive = over estimate. */
	variance: number;
	/** Whether any child work order has closed — gates the over/under readout,
	 *  which is meaningless before anything has been closed out. */
	hasClosedWork: boolean;
	/** Compact layout for tight panels (work order create). */
	size?: 'sm' | 'md';
	/** Optional extra classes merged onto the root. */
	className?: string;
}

/* ============================================
 * HELPERS
 * ============================================ */

/** Trim trailing zeros so 26.00 reads as "26" but 26.50 stays "26.5". */
const formatHours = (value: number): string =>
	Number.isFinite(value) ? String(Number(value.toFixed(2))) : '0';

/** Signed display for the over/under figure: "+6" over, "−5" under, "0" even. */
const formatVariance = (value: number): string => {
	const rounded = Number(value.toFixed(2));
	if (rounded === 0) return '0';
	return rounded > 0 ? `+${rounded}` : `−${Math.abs(rounded)}`;
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const ProjectHoursSummary: React.FC<ProjectHoursSummaryProps> = ({
	totalHours,
	estimatedOpen,
	actualUsed,
	remaining,
	projectedRemaining,
	variance,
	hasClosedWork,
	size = 'md',
	className = '',
}) => {
	const tileSize = size === 'sm' ? 'sm' : 'md';

	// A negative projection means the outstanding estimates already exceed what
	// is left in the pool — worth flagging before those jobs are worked.
	const isProjectedOver = projectedRemaining < 0;
	// Over estimate = red, under = green. Zero is neutral.
	const varianceTone = variance > 0 ? 'text-status-error' : variance < 0 ? 'text-status-success-dark' : 'text-brand-text-muted';

	return (
		<div className={`project-hours-summary ${className}`} data-ref="molecule-project-hours-summary">
			<div className="project-hours-summary__tiles grid grid-cols-2 tablet:grid-cols-4 gap-12px tablet:gap-16px">
				<StatTile className="project-hours-summary__tile" size={tileSize} value={formatHours(totalHours)} label="Total Hours" />
				<StatTile className="project-hours-summary__tile" size={tileSize} value={formatHours(estimatedOpen)} label="Estimated (open)" />
				<StatTile className="project-hours-summary__tile" size={tileSize} value={formatHours(actualUsed)} label="Actual Used" />
				<StatTile
					className="project-hours-summary__tile project-hours-summary__tile--remaining"
					size={tileSize}
					value={formatHours(remaining)}
					label="Remaining"
				/>
			</div>

			{/* Footnote row — the two numbers that need a sentence of context
			    rather than a bare tile. */}
			<div className="project-hours-summary__notes mt-12px space-y-4px">
				<p className="project-hours-summary__note text-12px font-sans text-brand-text-muted">
					Remaining is charged for closed work orders only — open estimates are earmarked, not spent.
					{' '}
					<span className={isProjectedOver ? 'font-semibold text-status-error' : 'font-medium text-brand-text'}>
						{formatHours(projectedRemaining)} hrs
					</span>
					{' '}projected after the {formatHours(estimatedOpen)} hrs still open.
				</p>

				{/* Over/under only means something once something has closed. */}
				{hasClosedWork && (
					<p className="project-hours-summary__variance text-12px font-sans text-brand-text-muted">
						Closed work orders came in{' '}
						<span className={`font-semibold ${varianceTone}`}>
							{formatVariance(variance)} hrs
						</span>{' '}
						{variance > 0 ? 'over' : variance < 0 ? 'under' : 'exactly on'} estimate.
					</p>
				)}
			</div>
		</div>
	);
};

export default ProjectHoursSummary;
